import { useEffect, useRef, useState } from "react";

export default function useMediaRecorder() {
  const [status, setStatus] = useState("idle"); // idle | recording | stopped
  const [recTime, setRecTime] = useState(0);
  const [stream, setStream] = useState(null);
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);
  const [previewBlocked, setPreviewBlocked] = useState(false);

  const previewRef = useRef(null);
  const recorderRef = useRef(null);
  const timerRef = useRef(null);

  const cleanupStream = () => {
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      setStream(null);
    }
  };

  const reset = () => {
    cleanupStream();
    if (url) {
      URL.revokeObjectURL(url);
      setUrl(null);
    }
    setBlob(null);
    setError(null);
    setRecTime(0);
    setStatus("idle");
    setPreviewBlocked(false);
  };

  const start = async () => {
    reset();

    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Video recording is not supported by this browser.");
      return;
    }

    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: true,
      });

      setError(null);
      setStream(s);
      setStatus("recording");
      setRecTime(0);

      const recorder = new MediaRecorder(s, { mimeType: "video/webm; codecs=vp8,opus" });
      recorderRef.current = recorder;
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = () => {
        const videoBlob = new Blob(chunks, { type: "video/webm" });
        const videoUrl = URL.createObjectURL(videoBlob);
        setBlob(videoBlob);
        setUrl(videoUrl);
        setStatus("stopped");
      };

      recorder.start(250);

      timerRef.current = setInterval(() => {
        setRecTime((t) => t + 1);
      }, 1000);

      // Attach stream to preview video element
      if (previewRef.current) {
        previewRef.current.srcObject = s;
        previewRef.current.muted = true;
        previewRef.current.playsInline = true;
        previewRef.current.autoplay = true;
        previewRef.current
          .play()
          .then(() => setPreviewBlocked(false))
          .catch(() => setPreviewBlocked(true));
      }
    } catch (err) {
      console.error(err);
      setError("Unable to access camera/microphone. Please allow permissions.");
    }
  };

  const stop = () => {
    if (recorderRef.current?.state === "recording") {
      recorderRef.current.stop();
    }
    cleanupStream();
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const enablePreview = async () => {
    if (!previewRef.current) return;
    try {
      await previewRef.current.play();
      setPreviewBlocked(false);
    } catch {
      setPreviewBlocked(true);
    }
  };

  useEffect(() => {
    return () => {
      stop();
      if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    status,
    recTime,
    previewRef,
    previewBlocked,
    stream,
    blob,
    url,
    error,
    start,
    stop,
    reset,
    enablePreview,
  };
}
